import CancelablePromise from 'mn-utils/CancelablePromise';
import {TFilter, TList, TUUID} from '@/types';
import {get, post, patch} from '../../http';
import {TListItem, TDetails} from './types';
import {getRetailerDetails, getRetailersList} from './mock';

export function apiAdminRetailersStatistic(params: TFilter<TListItem>): Promise<TList<TListItem>> {
  const url = '/f2c/v1/admin/accounts/overview';

  if (process.env.MOCK) {
    const response = {
      accounts_overview: {
        total: 0,
        this_month: 0
      }
    };
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url, params);
};

export function apiAdminRetailersList(params: TFilter<TListItem>): Promise<TList<TListItem>> {
  const url = '/f2c/v1/admin/accounts';
  params = params || {};
  params.offset = params.offset || 0;
  params.limit = params.limit || 50;

  if (process.env.MOCK) {
    const response = {
      list: getRetailersList(params.limit),
      count: params.limit * 10,
    };
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url, params);
}

export function apiAdminForceRetailerSignOut({merchant_id}) {
  return get(`/f2c/v1/admin/accounts/${merchant_id}/force-logout`, null, () => {
    return {};
  });
}

export function apiAdminForcePwdReset({merchant_id}) {
  return patch(`/f2c/v1/admin/accounts/${merchant_id}`, {must_change_password: true}, () => {
    return {};
  });
}

export function apiAdminRetailerGlobalConfigGet(params: {id: TUUID}): Promise<TDetails> {
  const url = '/f2c/v1/admin/configs/accounts.methods_allowed';

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url);
}

export function apiAdminRetailerGet(params: {id: TUUID}): Promise<TDetails> {
  const url = '/f2c/v1/admin/accounts/' + params.id;

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url);
}

export function apiAdminRetailerUpdate(props): Promise<TDetails> {
  const {id, ...params} = props;
  const url = '/f2c/v1/admin/accounts/' + id;

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return patch(url, params);
}

export function apiAdminRetailerFlowGet(params: {merchant_id: TUUID}): Promise<TDetails> {
  const url = '/f2c/v1/admin/merchant-gateway/list' + (params && params.merchant_id ? `?merchant_id=${params.merchant_id}` : '');

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url);
}

export function apiAdminRetailerFlowReset(params: {merchant_id: TUUID, password: string}): Promise<TDetails> {
  const url = '/f2c/v1/admin/merchant-gateway/reset';

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return post(url, params);
}

export function apiAdminRetailerFlowUpdate(params): Promise<TDetails> {
  const url = '/f2c/v1/admin/merchant-gateway/update';

  if (process.env.MOCK) {
    const response = {status: 'SUCCESS'};
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return post(url, params);
}

export function apiAdminMerchantGet(params: {id: TUUID}): Promise<TDetails> {
  const url = '/f2c/v1/admin/merchant/get?merchant_id=' + params.id;

  if (process.env.MOCK) {
    const response = getRetailerDetails();
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return get(url);
}

export function apiAdminMerchantUpdate(params): Promise<TDetails> {
  const url = '/f2c/v1/admin/merchant/update';

  if (process.env.MOCK) {
    const response = {status: 'SUCCESS'};
    console.info({url, params, response});
    return CancelablePromise.delay(500, response);
  }

  return post(url, params);
}

const apiAdminMerchantFeesCtor = ({endpoint, addId, fetchFN, mockResponse}) => (type) => (params) => {
  const {id = '', ...additionalParams} = (params || {});
  const url = `/f2c/v1/admin/merchant-fee/${type}/${endpoint}` + (addId && id ? `?merchant_id=${id}` : '');

  if (process.env.MOCK) {
    const response = mockResponse;
    console.info({url, params: additionalParams, response});
    return CancelablePromise.delay(500, response);
  }

  return fetchFN(url, addId ? additionalParams : params);
};

const apiAdminMerchantFeesGet = apiAdminMerchantFeesCtor({
  endpoint: 'list',
  addId: true,
  fetchFN: get,
  mockResponse: getRetailerDetails()
});
const apiAdminMerchantFeesReset = apiAdminMerchantFeesCtor({
  endpoint: 'reset',
  addId: false,
  fetchFN: post,
  mockResponse: getRetailerDetails()
});
const apiAdminMerchantFeesUpdate = apiAdminMerchantFeesCtor({
  endpoint: 'update',
  addId: false,
  fetchFN: post,
  mockResponse: {status: 'SUCCESS'}
});

const RETAILER_URL = 'retailer';

export const apiAdminMerchantRetailerFeesGet = apiAdminMerchantFeesGet(RETAILER_URL);
export const apiAdminMerchantRetailerFeesReset = apiAdminMerchantFeesReset(RETAILER_URL);
export const apiAdminMerchantRetailerFeesUpdate = apiAdminMerchantFeesUpdate(RETAILER_URL);

const CUSTOMER_URL = 'customer';

export const apiAdminMerchantCustomerFeesGet = apiAdminMerchantFeesGet(CUSTOMER_URL);
export const apiAdminMerchantCustomerFeesReset = apiAdminMerchantFeesReset(CUSTOMER_URL);
export const apiAdminMerchantCustomerFeesUpdate = apiAdminMerchantFeesUpdate(CUSTOMER_URL);
